import React, { useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";

import {
  DataTableSkeleton,
  SectionCard,
  StatusPill,
} from "components/admin";
import { DataTable } from "components/molecules/table/table";
import { useAuth } from "context/auth-context";
import { TeamPlayerAssignmentForm } from "pages/app/workspace/sections/forms/school-team-section-forms";
import { validateTeamPlayerAssignmentDraft } from "pages/app/workspace/sections/helpers/account-section.helpers";
import { workspaceApi } from "pages/app/workspace/services";
import { SchoolTeamProfileTabProps } from "pages/app/workspace/school-team-profile/school-team-profile.types";
import {
  SchoolTeamProfileContentStackSecondary,
  SchoolTeamProfileInfoRow,
  SchoolTeamProfileLabelText,
  SchoolTeamProfileMetaTextSecondary,
  SchoolTeamProfileThumbnailImage,
} from "pages/app/workspace/styles/school-team-profile.page.inline.styles";
import {
  filterAssignableSchoolTeamPlayers,
  isSchoolPlayerRegisteredForTeamSport,
  resolveTeamSportScope,
} from "pages/app/workspace/workspace-helpers";
import { getApiErrorMessage } from "utils/admin";
import { hasPortalAction } from "utils/portal-access";

export const SchoolTeamRosterTab: React.FC<SchoolTeamProfileTabProps> = ({
  schoolId,
  team,
  teamId,
}) => {
  const history = useHistory();
  const location = useLocation();
  const { portalAccess } = useAuth();
  const queryClient = useQueryClient();
  const [assignmentPlayerId, setAssignmentPlayerId] = useState("");
  const [hasAttemptedAssign, setHasAttemptedAssign] = useState(false);

  const canAssignPlayersToTeams = hasPortalAction(portalAccess, "school_team_players.manage");

  const teamPlayersQuery = useQuery({
    queryKey: ["school-team-profile-players", teamId],
    queryFn: () => workspaceApi.schoolTeamPlayers.list(teamId),
    enabled: Boolean(teamId),
  });
  const playersQuery = useQuery({
    queryKey: ["school-team-profile-player-pool", schoolId],
    queryFn: () => workspaceApi.schoolPlayers.list(schoolId),
    enabled: Boolean(schoolId) && canAssignPlayersToTeams,
  });
  const sportsQuery = useQuery({
    queryKey: ["school-team-profile-sports", schoolId],
    queryFn: () => workspaceApi.schoolSports.list(schoolId),
    enabled: Boolean(schoolId) && canAssignPlayersToTeams,
  });

  const roster = useMemo(() => teamPlayersQuery.data || [], [teamPlayersQuery.data]);
  const teamSportScope = useMemo(
    () => resolveTeamSportScope(team, sportsQuery.data || []),
    [sportsQuery.data, team],
  );
  const schoolPlayersAtSchool = useMemo(
    () =>
      (playersQuery.data || []).filter((player: any) => {
        if (!player.school_id) {
          return true;
        }

        return String(player.school_id) === String(schoolId);
      }),
    [playersQuery.data, schoolId],
  );
  const sportEligiblePlayers = useMemo(
    () =>
      schoolPlayersAtSchool.filter((player: any) =>
        isSchoolPlayerRegisteredForTeamSport(player, teamSportScope),
      ),
    [schoolPlayersAtSchool, teamSportScope],
  );
  const assignablePlayers = useMemo(
    () =>
      filterAssignableSchoolTeamPlayers({
        players: playersQuery.data || [],
        resolvedSchoolId: schoolId,
        roster,
        schoolSports: sportsQuery.data || [],
        team,
      }),
    [playersQuery.data, roster, schoolId, sportsQuery.data, team],
  );
  const rosterEmptyState = useMemo(() => {
    if (schoolPlayersAtSchool.length === 0) {
      return "no-school-players" as const;
    }

    if (sportEligiblePlayers.length === 0) {
      return "no-sport-registration" as const;
    }

    return "all-on-roster" as const;
  }, [schoolPlayersAtSchool.length, sportEligiblePlayers.length]);

  const assignmentErrors = validateTeamPlayerAssignmentDraft({
    selectedPlayerId: assignmentPlayerId,
    selectedTeamId: String(teamId || ""),
  });
  const assignmentPlayerError = hasAttemptedAssign ? assignmentErrors.selectedPlayerId : undefined;

  const assignPlayerMutation = useMutation({
    mutationFn: () =>
      workspaceApi.schoolTeamPlayers.create(teamId, {
        player_id: assignmentPlayerId,
      }),
    onSuccess: async () => {
      toast.success("Player assigned to team.");
      setAssignmentPlayerId("");
      setHasAttemptedAssign(false);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["school-team-profile-players", teamId] }),
        queryClient.invalidateQueries({ queryKey: ["school-team-profile-dashboard", teamId] }),
        queryClient.invalidateQueries({ queryKey: ["workspace-school-teams", schoolId] }),
      ]);
    },
    onError: (error: unknown) => {
      toast.error(getApiErrorMessage(error, "Unable to assign this player to the team."));
    },
  });

  const handleAssignPlayerSubmit = () => {
    setHasAttemptedAssign(true);
    const firstError = assignmentErrors.selectedTeamId || assignmentErrors.selectedPlayerId;
    if (firstError) {
      toast.error(firstError);
      return;
    }
    assignPlayerMutation.mutate();
  };

  const handleAssignmentPlayerChange = (playerId: string) => {
    setAssignmentPlayerId(playerId);
    if (playerId) {
      setHasAttemptedAssign(false);
    }
  };

  return (
    <SectionCard subtitle="Manage squad membership and review players linked to this team." title="Roster">
      <SchoolTeamProfileContentStackSecondary>
        {canAssignPlayersToTeams ? (
          <TeamPlayerAssignmentForm
            assignablePlayers={assignablePlayers}
            emptyState={rosterEmptyState}
            isLoadingPlayers={playersQuery.isLoading || sportsQuery.isLoading}
            isSubmitting={assignPlayerMutation.isPending}
            onPlayerChange={handleAssignmentPlayerChange}
            onSubmit={handleAssignPlayerSubmit}
            schoolName={team.school_name}
            selectedPlayerError={assignmentPlayerError}
            selectedPlayerId={assignmentPlayerId}
            sportName={teamSportScope.sportName}
            teamName={team.name}
          />
        ) : null}
        <DataTable
          canEdit={false}
          canRemove={false}
          columns={[
            {
              label: "Player",
              accessor: (row: any) => (
                <SchoolTeamProfileInfoRow>
                  <SchoolTeamProfileThumbnailImage
                    alt={row.full_name || "Player"}
                    fallbackLabel={row.full_name}
                    src={row.avatar_url || row.image_url || row.image}
                    variant="athlete"
                  />
                  <div>
                    <SchoolTeamProfileLabelText>
                      {row.full_name || "Unknown"}
                    </SchoolTeamProfileLabelText>
                    <SchoolTeamProfileMetaTextSecondary>
                      {row.school_grade || "Grade pending"}
                    </SchoolTeamProfileMetaTextSecondary>
                  </div>
                </SchoolTeamProfileInfoRow>
              ),
            },
            {
              label: "Position",
              accessor: (row: any) => row.position || "Unknown",
            },
            {
              label: "Jersey",
              accessor: (row: any) => row.jersey_number || "Not set",
            },
            {
              label: "Consent",
              accessor: (row: any) => <StatusPill value={row.consent_status || "pending"} />,
            },
          ]}
          data={roster}
          emptyDescription="Players assigned to this team will appear here."
          emptyTitle="No roster yet"
          hideNav
          isLoading={teamPlayersQuery.isLoading}
          loadingState={<DataTableSkeleton columns={4} rows={4} showThumbnail />}
          onView={(playerId) => {
            if (schoolId) {
              history.push({
                pathname: `/workspace/school-players/${schoolId}/${playerId}`,
                state: {
                  backTo: `${location.pathname}${location.search}`,
                },
              });
              return;
            }
            history.push(`/athletes/${playerId}`);
          }}
        />
      </SchoolTeamProfileContentStackSecondary>
    </SectionCard>
  );
};
